import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, Gift, CheckCircle2, FileText, ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";

export const ENVELOPE_OPTIONS = [
  {
    id: "anniversary",
    title: "Happy Anniversary ♡",
    subtitle: "Celebrations & Milestone Love",
    tag: "Most Popular",
    image: "/letters/anniversary.jpg",
    color: "#E5497C",
    bgTint: "#FFF0F3",
    borderColor: "#F7C7DC",
    desc: "Crafted on luxury ivory linen parchment with a deep crimson sheer organza bow."
  },
  {
    id: "birthday",
    title: "Happy Birthday",
    subtitle: "Warm Wishes & Special Milestones",
    tag: "Celebration",
    image: "/letters/birthday.jpg",
    color: "#C4A55A",
    bgTint: "#FAF5E8",
    borderColor: "#E8DFD0",
    desc: "Elegant dusty rose organza ribbon tied over hand-lettered birthday calligraphy."
  },
  {
    id: "sorry",
    title: "I am sorry ♡",
    subtitle: "Reconnection, Apology & Healing",
    tag: "Heartfelt",
    image: "/letters/sorry.jpg",
    color: "#8B263E",
    bgTint: "#FBF0F2",
    borderColor: "#E8C5CE",
    desc: "Soft ivory envelope with deep velvet red bow — says what words alone cannot."
  },
  {
    id: "congratulations",
    title: "Congratulations! ♡",
    subtitle: "Achievements, Weddings & Success",
    tag: "Joyous",
    image: "/letters/congratulations.jpg",
    color: "#1C1924",
    bgTint: "#F4F0FF",
    borderColor: "#DDD4F0",
    desc: "Grand celebratory crimson bow with refined burgundy copperplate lettering."
  }
];

export default function PackagingShowcase() {
  const { letterStyle, setLetterStyle, note, setNote } = useStore();
  const [selectedId, setSelectedId] = useState(letterStyle || "anniversary");

  const current = ENVELOPE_OPTIONS.find((e) => e.id === selectedId) || ENVELOPE_OPTIONS[0];

  const handleSelect = (id) => {
    setSelectedId(id);
    setLetterStyle(id);
  };

  return (
    <section data-testid="packaging-showcase-section" className="relative py-24 md:py-32 bg-[#FCF9F5] overflow-hidden grain-overlay">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF0F3] border border-[#F7C7DC] mb-4">
            <Sparkles size={14} className="text-[#E5497C]" />
            <span className="text-[12px] font-bold tracking-[0.2em] uppercase text-[#E5497C]">Signature Unboxing Experience</span>
          </div>
          <h2 className="font-display font-semibold text-[#1C1924] text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            Wrapped in silk, <br className="hidden sm:inline" />
            <span className="font-serif-italic text-[#E5497C] font-normal">sealed with handwritten love.</span>
          </h2>
          <p className="mt-4 text-[#4A4652] text-[16px] md:text-[18px] leading-relaxed">
            Every Alaira order includes our bespoke hand-lettered envelope with a hand-tied organza ribbon bow. Select your envelope style below to tuck your custom message inside.
          </p>
        </div>

        {/* Main Grid Interactive Showcase */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          
          {/* Left Column: Envelope Option Tabs */}
          <div className="lg:col-span-6 space-y-4">
            <p className="text-[12px] uppercase tracking-widest font-bold text-[#8b8790] mb-2 font-body">Choose Your Hand-Lettered Envelope</p>
            {ENVELOPE_OPTIONS.map((env) => {
              const isSelected = selectedId === env.id;
              return (
                <button
                  key={env.id}
                  data-testid={`envelope-option-${env.id}`}
                  onClick={() => handleSelect(env.id)}
                  className={`w-full text-left p-4 sm:p-5 rounded-[22px] transition-all duration-300 flex items-center justify-between border cursor-pointer ${
                    isSelected
                      ? "bg-white shadow-xl scale-[1.02] border-[#1C1924] ring-2 ring-[#1C1924]/10"
                      : "bg-white/60 hover:bg-white border-[#EEE7FA] hover:shadow-md"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={env.image}
                      alt={env.title}
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover shadow-sm border border-[#EEE7FA]"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-display font-bold text-[15px] sm:text-[16.5px] text-[#1C1924]">{env.title}</h4>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: env.bgTint, color: env.color }}>
                          {env.tag}
                        </span>
                      </div>
                      <p className="text-[12.5px] text-[#8b8790] mt-0.5">{env.subtitle}</p>
                    </div>
                  </div>

                  <div className={`w-7 h-7 rounded-full grid place-items-center transition shrink-0 ${isSelected ? "bg-[#1C1924] text-white" : "bg-[#F4F0FF] text-[#8b8790]"}`}>
                    {isSelected ? <CheckCircle2 size={16} /> : <Heart size={14} />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Column: High Resolution Envelope Display */}
          <div className="lg:col-span-6">
            <div className="relative rounded-[32px] overflow-hidden glass-card p-4 sm:p-6 shadow-2xl border border-white/80">
              
              {/* Photo Viewport */}
              <div className="relative aspect-[4/5] sm:aspect-[4/3] rounded-[24px] overflow-hidden bg-[#F0EAE1]">
                <img
                  key={current.image}
                  src={current.image}
                  alt={current.title}
                  className="w-full h-full object-cover transition-all duration-700 hover:scale-105"
                />
                
                {/* Overlay Badge */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md border border-white/80 rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
                  <Heart size={14} className="fill-[#E5497C] text-[#E5497C]" />
                  <span className="text-[12.5px] font-bold text-[#1C1924] font-body">{current.title}</span>
                </div>

                <div className="absolute bottom-4 right-4 bg-[#1C1924]/90 backdrop-blur-md text-white text-[11px] font-semibold px-3 py-1.5 rounded-full">
                  Included Free With Your Order ✨
                </div>
              </div>

              {/* Description & Note Status */}
              <div className="mt-6 p-4 bg-white rounded-2xl border border-[#EEE7FA]">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold uppercase tracking-wider text-[#C4A55A] font-body font-semibold">Crafting Standard</span>
                  <span className="text-[12px] text-[#8b8790]">Silk Organza & Ivory Parchment</span>
                </div>
                <p className="text-[13.5px] text-[#4A4652] mt-1 font-body">{current.desc}</p>
                
                <div className="mt-4 pt-3 border-t border-[#EEE7FA] flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[13px] text-[#1C1924] font-semibold">
                    <FileText size={16} className="text-[#E5497C]" />
                    <span>Your custom letter will be sealed inside</span>
                  </div>
                  <Link
                    to="/checkout"
                    className="inline-flex items-center gap-1 text-[13px] font-bold text-[#E5497C] hover:underline"
                  >
                    Customize in Checkout <ArrowRight size={14} />
                  </Link>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* Highlights Row */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Gift, title: "100% Free Packaging", desc: "No hidden wrap charges — signature packaging is complimentary." },
            { icon: Heart, title: "Hand-Tied Silk Ribbon", desc: "Crafted individually by our artisans in soft satin and organza." },
            { icon: Sparkles, title: "Handwritten Calligraphy", desc: "Original copperplate calligraphy lettering on every envelope." },
            { icon: CheckCircle2, title: "Sealed Inside Gift Box", desc: "Safely nestled alongside your items, pristine upon unboxing." }
          ].map((h, idx) => (
            <div key={idx} className="bg-white/80 backdrop-blur-md rounded-[20px] p-5 border border-[#EEE7FA] shadow-sm flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-2xl bg-[#FFF0F3] text-[#E5497C] grid place-items-center shrink-0">
                <h.icon size={20} />
              </div>
              <div>
                <h5 className="font-display font-bold text-[14.5px] text-[#1C1924]">{h.title}</h5>
                <p className="text-[12.5px] text-[#8b8790] mt-0.5 leading-snug">{h.desc}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
